import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { PLAGIARISM_API } from '../apiConfig';
// css
import './plagiarism.css';
// CSS
import '../Css/BeamProperties.css';
import '../Css/BeamDeflection.css';
import '../Css/NumberLine.css';
import '../Css/AboutUS.css';
import '../Css/Navbar.css';
import '../Css/Padeye.css';

// modules
import { Link } from 'react-router-dom';
import axios from 'axios';

// icons 
import { GrLinkTop } from "react-icons/gr";

export default function Plagiarism() {

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const [text, setText] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  const handleAnalyze = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${PLAGIARISM_API}/check_plagiarism`, { text });
      setAnalysis(res.data);
    } catch (err) {
      console.error(err);
      alert('An error occurred while fetching analysis.');
    } finally {
      setLoading(false);
    }
  };

  const [ballCount, setBallCount] = useState(0);
  const ballRefs = useRef([]);

  useEffect(() => {
    const spacingVH = 70;

    const calculateBallCount = () => {
      const pageHeight = document.documentElement.scrollHeight;
      const vh = window.innerHeight / 100;
      const spacingPx = spacingVH * vh; // convert spacingVH to px
      const count = Math.ceil(pageHeight / spacingPx); // cover entire page height
      setBallCount(count);
    };

    calculateBallCount();
    window.addEventListener('resize', calculateBallCount);
    window.addEventListener('scroll', calculateBallCount);

    const observer = new MutationObserver(calculateBallCount);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('resize', calculateBallCount);
      window.removeEventListener('scroll', calculateBallCount);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    ballRefs.current.forEach(ball => ball && observer.observe(ball));

    return () => observer.disconnect();
  }, [ballCount]);

  const radius = 25;
  const stroke = 5;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;

  // Active state for tool scan options
  const [scanOptions, setScanOptions] = useState({
    web: true,
    academic: false,
    ai: false,
  });

  // Toggle active options
  const toggleOption = (opt) => {
    setScanOptions(prev => ({
      ...prev,
      [opt]: !prev[opt]
    }));
  };

  // Word counter calculation
  const getWordCount = () => {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  return (
    <>
      <Helmet>
        <title>Free Plagiarism Checker Online – Instant Duplicate Content Detector | OOK Calculator</title>
        <meta
          name="description"
          content="Check for plagiarism instantly with OOK Calculator's free online plagiarism checker. Detect duplicate content, paraphrased text & AI-generated writing. Accurate, fast & trusted by students, teachers & professionals."
        />
        <meta name="keywords" content="plagiarism checker, free plagiarism checker, online plagiarism detector, duplicate content checker, check for plagiarism, plagiarism tool, plagiarism scanner, check plagiarism online, academic plagiarism checker, plagiarism detection tool" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="OOK Calculator" />
        <link rel="canonical" href="https://ook-calculator.com/Plagiarism" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Free Plagiarism Checker",
            "url": "https://ook-calculator.com/Plagiarism",
            "description": "A free online plagiarism checker that detects duplicate content, paraphrased text, and AI-generated writing instantly.",
            "applicationCategory": "UtilityApplication",
            "operatingSystem": "All",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "publisher": {
              "@type": "Organization",
              "name": "OOK Calculator",
              "url": "https://ook-calculator.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://ook-calculator.com/favicon.png"
              }
            },
            "featureList": [
              "Plagiarism detection",
              "Duplicate content scanning",
              "AI-generated text detection",
              "Paraphrase detection",
              "Instant results",
              "Free to use"
            ]
          }
          `}
        </script>
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Is this plagiarism checker free to use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, OOK Calculator's plagiarism checker is completely free. ."
                }
              },
              {
                "@type": "Question",
                "name": "How does the plagiarism checker work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our tool scans your text against billions of web pages, academic databases, and published content to identify matching or similar passages, giving you a similarity percentage and source breakdown."
                }
              },
              {
                "@type": "Question",
                "name": "Can the plagiarism checker detect AI-generated content?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. OOK Calculator's checker includes AI content detection alongside traditional plagiarism scanning, identifying text generated by tools like ChatGPT, Gemini, or Claude."
                }
              },
              {
                "@type": "Question",
                "name": "How many words can I check at once?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can check up to 1,000 words per scan for free. For longer documents, you can split the text into sections."
                }
              },
              {
                "@type": "Question",
                "name": "Is my text stored or shared after checking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. We do not store, sell, or share any text you submit. Your content is processed securely and discarded immediately after the scan."
                }
              }
            ]
          }
          `}
        </script>
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://ook-calculator.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Plagiarism Checker",
                "item": "https://ook-calculator.com/Plagiarism"
              }
            ]
          }
          `}
        </script>
      </Helmet>

      <div className="plagiarism-theme-wrapper">
        {/* BREADCRUMB */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <a href="https://ook-calculator.com">Home</a>
          <span aria-hidden="true">›</span>
          <span aria-current="page">Plagiarism Checker</span>
        </nav>

        {/* HERO */}
        <header className="hero" role="banner">
          {/* <div className="badge" aria-label="Tool category">Free Online Tool</div> */}
          <h1>
            Free <em>Plagiarism Checker</em> — Detect Duplicate Content Instantly
          </h1>
          <p className="hero-sub">
            Paste your text below to compare it against a large collection of web content and academic sources in seconds.
          </p>
          <div className="trust-row" aria-label="Tool highlights">
            <span className="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Free to use
            </span>
            <span class="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Instant results
            </span>
            <span class="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Private & secure
            </span>
          </div>
        </header>

        {/* BACKGROUND BALL ANIMATION */}
        <div className="ball-section">
          {Array.from({ length: ballCount }, (_, i) => (
            <div
              key={i}
              ref={el => (ballRefs.current[i] = el)}
              className={`ball ball-${(i % 10) + 1}`}
              style={{
                position: 'absolute',
                top: `${i * 70}vh`,
                left: i % 2 === 0 ? '0%' : '97%',
                transform: 'translateX(-50%)',
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        {/* TOOL WIDGET */}
        <main id="main-content">
          <section className="tool-section" aria-label="Plagiarism checker tool">
            <div className="tool-card" role="region" aria-label="Text input and scan">
              <div className="tool-header">
                <div className="tool-header-left">
                  <div className="dot dot-r" aria-hidden="true"></div>
                  <div className="dot dot-y" aria-hidden="true"></div>
                  <div className="dot dot-g" aria-hidden="true"></div>
                  <span className="tool-title">Plagiarism Checker — OOK Calculator</span>
                </div>
                <span className="word-count" id="wordCount" aria-live="polite">
                  {getWordCount()} / 1000 words
                </span>
              </div>
              <label htmlFor="inputText" className="sr-only">Enter text to check for plagiarism</label>
              <textarea
                id="inputText"
                placeholder="Paste or type your text here to check for plagiarism… (up to 1,000 words for free)"
                aria-label="Text to check for plagiarism"
                aria-describedby="wordCount"
                value={text}
                onChange={(e) => setText(e.target.value)}
              ></textarea>
              <div className="tool-footer">
                <div className="tool-options" role="group" aria-label="Scan options">
                  <button
                    type="button"
                    className={`opt-tag ${scanOptions.web ? 'active' : ''}`}
                    aria-pressed={scanOptions.web}
                    onClick={() => toggleOption('web')}
                  >
                    Web Sources
                  </button>
                  <button
                    type="button"
                    className={`opt-tag ${scanOptions.academic ? 'active' : ''}`}
                    aria-pressed={scanOptions.academic}
                    onClick={() => toggleOption('academic')}
                  >
                    Academic DB
                  </button>
                  <button
                    type="button"
                    className={`opt-tag ${scanOptions.ai ? 'active' : ''}`}
                    aria-pressed={scanOptions.ai}
                    onClick={() => toggleOption('ai')}
                  >
                    AI Detection
                  </button>
                </div>
                <button
                  onClick={handleAnalyze}
                  disabled={loading || !text.trim()}
                  className="cta-btn"
                  aria-label="Check text for plagiarism"
                >
                  {loading ? 'Analyzing...' : 'Check Plagiarism →'}
                </button>
              </div>
            </div>
          </section>

          {/* DYNAMIC RESULTS SECTION */}
          {analysis && (
            <section className="results-section" aria-label="Analysis report results">
              <div className="results-grid">
                {/* Circular gauges card */}
                <div className="results-card">
                  <h2 className="results-title">Similarity Score</h2>
                  <div className="circles-container">
                    {[
                      { label: 'Plagiarism', color: '#ff6b6b', value: analysis.total_percent || 0 },
                      { label: 'Unique', color: '#00d9a6', value: 100 - (analysis.total_percent || 0) },
                      { label: 'Exact Match', color: '#ff5f57', value: analysis.exact_percent || 0 },
                      { label: 'Partial Match', color: '#febc2e', value: analysis.partial_percent || 0 },
                    ].map((item, index) => {
                      const value = item.value;
                      const offset = circumference - (value / 100) * circumference;
                      return (
                        <div key={index} className="circle-stat">
                          <svg height={radius * 2} width={radius * 2} style={{ display: "block", margin: "auto" }}>
                            <circle
                              stroke="#22222e"
                              fill="transparent"
                              strokeWidth={stroke}
                              r={normalizedRadius}
                              cx={radius}
                              cy={radius}
                            />
                            <circle
                              stroke={item.color}
                              fill="transparent"
                              strokeWidth={stroke}
                              strokeLinecap="round"
                              strokeDasharray={`${circumference} ${circumference}`}
                              strokeDashoffset={offset}
                              r={normalizedRadius}
                              cx={radius}
                              cy={radius}
                              transform={`rotate(-90 ${radius} ${radius})`}
                              style={{ transition: "stroke-dashoffset 0.35s" }}
                            />
                            <text
                              x="50%"
                              y="50%"
                              dominantBaseline="middle"
                              textAnchor="middle"
                              fontSize="11"
                              fontWeight="bold"
                              fill="#f0f0f8"
                            >
                              {value}%
                            </text>
                          </svg>
                          <div className="circle-label">{item.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Match details card */}
                <div className="results-card">
                  <h2 className="results-title">Matches & Sources</h2>
                  <div className="matches-list">
                    {analysis.matches && analysis.matches.length > 0 ? (
                      analysis.matches.map((item, idx) => (
                        <div key={idx} className="match-item">
                          <div className="match-sentence">📝 Sentence {idx + 1}: {item.sentence}</div>
                          {item.source && (
                            <a
                              href={item.source}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="match-source"
                            >
                              View Source URL →
                            </a>
                          )}
                          <p className="match-snippet">{item.snippet}</p>
                          <div className="match-meta">
                            Match Type: {item.match_type.toUpperCase()}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: 'var(--muted)' }}>No matches detected. Your content appears 100% original!</p>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* STATS */}
          <div className="stats-grid" role="list" aria-label="Usage statistics">
            <div className="stat-cell" role="listitem">
              <div className="stat-num">1M+</div>
              <div className="stat-label">Texts Scanned</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">99.8%</div>
              <div className="stat-label">Detection Accuracy</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">5B+</div>
              <div className="stat-label">Web Pages Indexed</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">&lt;6s</div>
              <div className="stat-label">Average Scan Time</div>
            </div>
          </div>

          {/* WHAT IS SECTION */}
          <section className="content-wrap section" aria-labelledby="what-is-heading">
            <h2 id="what-is-heading">What Is a Plagiarism Checker?</h2>
            <p></p>
            <p>
              A <strong>plagiarism checker</strong> is an online tool that compares your submitted text against a vast database of web pages, academic publications, news articles, and other published content. It highlights any sections that closely match existing material and provides a <strong>similarity score</strong>, helping you ensure your content is original before submitting, publishing, or sharing.
            </p>
            <p>
              OOK Calculator's free plagiarism detector goes beyond simple copy-paste detection. It uses advanced algorithms to identify <strong>paraphrased plagiarism</strong>, <strong>mosaic plagiarism</strong> (where sentences are stitched from multiple sources), and <strong>AI-generated content</strong> — giving you a complete picture of your text's originality.
            </p>
          </section>

          {/* FEATURES */}
          <section className="content-wrap section" aria-labelledby="features-heading">
            <h2 id="features-heading">Key Features of Our Plagiarism Detection Tool</h2>
            <p></p>
            <p>Everything you need to ensure 100% original content — in one place, for free.</p>
            <div className="features-grid">
              <article className="feature-card">
                <div className="feature-icon fi-purple" aria-hidden="true">🔍</div>
                <h3>Deep Web Scanning</h3>
                <p>Cross-references your text against over 5 billion indexed web pages for comprehensive duplicate detection.</p>
              </article>
              <article class="feature-card">
                <div class="feature-icon fi-green" aria-hidden="true">🎓</div>
                <h3>Academic Database Check</h3>
                <p>Scans published journals, theses, research papers, and academic archives to catch scholarly plagiarism.</p>
              </article>
              <article class="feature-card">
                <div class="feature-icon fi-red" aria-hidden="true">🤖</div>
                <h3>AI Content Detection</h3>
                <p>Identifies text generated by ChatGPT, Gemini, Claude, and other AI tools — essential for authentic submissions.</p>
              </article>
              <article class="feature-card">
                <div class="feature-icon fi-blue" aria-hidden="true">📊</div>
                <h3>Similarity Score Report</h3>
                <p>Receive a clear percentage-based similarity score with highlighted passages and source links.</p>
              </article>
              <article class="feature-card">
                <div class="feature-icon fi-orange" aria-hidden="true">⚡</div>
                <h3>Instant Results</h3>
                <p>Get full plagiarism reports in under 6 seconds. No waiting, no queues — results appear immediately.</p>
              </article>
              <article class="feature-card">
                <div class="feature-icon fi-pink" aria-hidden="true">🔒</div>
                <h3>100% Private & Secure</h3>
                <p>Your text is never stored, shared, or indexed. All scans are processed securely and discarded after completion.</p>
              </article>
            </div>
          </section>

          {/* HOW IT WORKS */}
          <section className="content-wrap section" aria-labelledby="how-it-works-heading">
            <h2 id="how-it-works-heading">How to Check for Plagiarism – 3 Simple Steps</h2>
            <p></p>
            <p>Our tool is designed for speed and simplicity. No technical knowledge needed.</p>
            <div className="steps">
              <div className="step">
                <div className="step-num" aria-hidden="true">01</div>
                <h3>Paste Your Text</h3>
                <p>Copy and paste your essay, article, blog post, or any other content into the text box above. Up to 1,000 words per scan.</p>
              </div>
              <div className="step">
                <div className="step-num" aria-hidden="true">02</div>
                <h3>Select Scan Options</h3>
                <p>Choose to check against web sources, academic databases, or enable AI detection — or scan all three simultaneously.</p>
              </div>
              <div className="step">
                <div className="step-num" aria-hidden="true">03</div>
                <h3>Review Your Report</h3>
                <p>Within seconds, view your similarity score, highlighted duplicate passages, matched sources, and actionable suggestions.</p>
              </div>
            </div>
          </section>

          {/* WHO IT'S FOR */}
          <section className="content-wrap section" aria-labelledby="use-cases-heading">
            <h2 id="use-cases-heading">Who Uses Our Plagiarism Checker?</h2>
            <p></p>
            <p>OOK Calculator's plagiarism tool serves a wide range of users across academia, business, and content creation.</p>
            <div className="use-cases">
              <div className="use-card">
                <span className="use-emoji" aria-hidden="true">📚</span>
                <div>
                  <h3>Students</h3>
                  <p>Verify essays, research papers, and dissertations before submission to avoid accidental plagiarism.</p>
                </div>
              </div>
              <div className="use-card">
                <span className="use-emoji" aria-hidden="true">🏫</span>
                <div>
                  <h3>Teachers & Professors</h3>
                  <p>Quickly assess student submissions for originality and identify unattributed sources.</p>
                </div>
              </div>
              <div className="use-card">
                <span className="use-emoji" aria-hidden="true">✍️</span>
                <div>
                  <h3>Content Writers</h3>
                  <p>Ensure blog posts, articles, and web copy are 100% unique before publishing to clients or platforms.</p>
                </div>
              </div>
              <div className="use-card">
                <span className="use-emoji" aria-hidden="true">📰</span>
                <div>
                  <h3>Journalists</h3>
                  <p>Confirm that reported pieces contain original writing and properly attribute all sourced material.</p>
                </div>
              </div>
              <div className="use-card">
                <span className="use-emoji" aria-hidden="true">💼</span>
                <div>
                  <h3>Businesses & SEO Teams</h3>
                  <p>Protect website rankings by ensuring all published content is original and free of duplicate penalties.</p>
                </div>
              </div>
              <div className="use-card">
                <span className="use-emoji" aria-hidden="true">🔬</span>
                <div>
                  <h3>Researchers</h3>
                  <p>Maintain academic integrity in papers, grant proposals, and published findings.</p>
                </div>
              </div>
            </div>
          </section>

          {/* WHY CHOOSE US */}
          <section className="content-wrap section" aria-labelledby="why-choose-heading">
            <h2 id="why-choose-heading">Why Choose OOK Calculator's Plagiarism Checker?</h2>
            <p></p>
            <p>
              There are dozens of plagiarism checkers available — but most are slow, expensive, or require account creation. OOK Calculator offers a truly <strong>free, no-login, no-limit</strong> plagiarism scanning experience powered by the same technology used by professionals.
            </p>
            <p>
              Unlike tools that only detect copy-pasted text, our checker uses <strong>semantic similarity analysis</strong> to catch paraphrased content — the type most plagiarism detectors miss. Combined with our integrated <strong>AI writing detector</strong>, you get the most comprehensive originality check available online today.
            </p>
            <p>
              We also pair our plagiarism checker with a full suite of writing tools including a <strong>Paraphrase tool</strong>, <strong>Humanizer</strong>, and <strong>AI Detector</strong> — so you can detect issues <em>and</em> fix them in the same platform, without switching tabs.
            </p>
          </section>

          {/* FAQ */}
          <section className="content-wrap section" aria-labelledby="faq-heading">
            <h2 id="faq-heading">Frequently Asked Questions About Plagiarism Checking</h2>
            <p></p>
            <div className="faq-list">
              {[
                { q: 'Is this plagiarism checker free to use?', a: "Yes, OOK Calculator's plagiarism checker is completely free. Simply paste your text and click scan." },
                { q: 'How does the plagiarism checker work?', a: 'Our tool scans your text against billions of web pages, academic databases, and published content to identify matching or similar passages. It returns a similarity percentage with highlighted matches and source URLs so you can review and address any issues.' },
                { q: 'Can the plagiarism checker detect AI-generated content?', a: "Yes. OOK Calculator's checker includes AI content detection alongside traditional plagiarism scanning. It can identify text generated by tools such as ChatGPT, Gemini, and Claude with high accuracy." },
                { q: 'What is a good plagiarism score?', a: 'A similarity score of 0–10% is generally considered excellent and original. 10–20% may be acceptable depending on the context (such as naturally common phrases or properly cited quotes). Anything above 20% typically warrants review and revision.' },
                { q: 'How many words can I check at once?', a: 'You can check up to 1,000 words per scan for free. For longer documents, simply split the text into sections and run multiple checks.' },
                { q: 'Is my text stored or shared after checking?', a: 'No. We do not store, sell, or share any text you submit. All content is processed securely and permanently discarded immediately after the scan completes.' },
                { q: 'What types of plagiarism can this tool detect?', a: 'OOK Calculator can detect direct copy-paste plagiarism, paraphrased plagiarism, mosaic plagiarism (combining text from multiple sources), and AI-generated content — covering all major forms of originality concerns.' },
              ].map((item, i) => (
                <details key={i} open={activeFaq === i} onClick={(e) => { e.preventDefault(); setActiveFaq(activeFaq === i ? null : i); }}>
                  <summary>{item.q}</summary>
                  <div className="faq-answer">{item.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* RELATED TOOLS */}
          <section className="content-wrap section" aria-labelledby="related-tools-heading">
            <h2 id="related-tools-heading">Explore More Free Writing Tools</h2>
            <p></p>
            <p>OOK Calculator offers a complete AI writing toolkit — detect, improve, and transform your content.</p>
            <nav className="related-grid" aria-label="Related tools">
              <a href="https://ook-calculator.com/AIDetector" className="related-card">
                <span aria-hidden="true">🤖</span>
                <span>AI Content Detector</span>
              </a>
              <a href="https://ook-calculator.com/Paraphrase" className="related-card">
                <span aria-hidden="true">✏️</span>
                <span>Paraphrase Tool</span>
              </a>
              <a href="https://ook-calculator.com/Humanize" className="related-card">
                <span aria-hidden="true">🧑</span>
                <span>AI Humanizer</span>
              </a>
              <a href="https://ook-calculator.com/GrammarChecker" className="related-card">
                <span aria-hidden="true">📝</span>
                <span>Grammar Checker</span>
              </a>
              <a href="https://ook-calculator.com/Summarizer" className="related-card">
                <span aria-hidden="true">📋</span>
                <span>Text Summarizer</span>
              </a>
              <a href="https://ook-calculator.com" className="related-card">
                <span aria-hidden="true">🧮</span>
                <span>All Tools</span>
              </a>
            </nav>
          </section>
        </main>

        {/* SCROLL TO TOP */}
        <section className="cse-header-top">
          <Link smooth="true" duration={500} offset={-70} onClick={scrollToTop} aria-label="Scroll to top">
            <GrLinkTop className="" />
          </Link>
        </section>
      </div>
    </>
  );
}
